import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { EventDto } from '@/api/services/api/generated/data-contracts';
import { cn } from '@/lib/utils';
import { useDialog } from '@/ui/components/alert-dialog/alert-dialog-provider';
import { Dropdown, DropdownItem, DropdownItemContent } from '@/ui/components/arandu/dropdown';
import { IconButton } from '@/ui/components/arandu/icon-button';
import { Switch } from '@/ui/components/arandu/switch';
import { Icon } from '@/ui/components/icon';
import { useDeleteEvent, useToggleEventPrivacy } from '@/ui/hooks/use-event';

import { useCopyToClipboard } from '../hooks/use-copy-to-clipboard';
import { userKeys } from '../hooks/use-user-events';

interface EventOverflowMenuProps {
  eventStatus: EventDto['status'];
  eventId: string;
  options: {
    tooglePrivate?: boolean;
    eventPanel?: boolean;
    viewEvent?: boolean;
    delete?: boolean;
    edit?: boolean;
  };
}

export const EventOverflowMenu = ({ eventId, eventStatus, options }: EventOverflowMenuProps) => {
  const { t } = useTranslation();
  const dialog = useDialog();

  const { mutate: deleteEvent } = useDeleteEvent(eventId);
  const { mutate: toggleEventPrivacy, isPending } = useToggleEventPrivacy(eventId);

  const { pathname } = useLocation();
  const isEventListPage = pathname === '/events';

  const nav = useNavigate();

  const openDeleteDialog = () => {
    dialog.open({
      title: t('dialogs.delete-event.title'),
      description: t('dialogs.delete-event.description'),
      action: {
        title: t('delete-event'),
        onClick: () =>
          deleteEvent(undefined, {
            onSuccess: () => {
              dialog.close();
              if (!isEventListPage) nav('/events');
            },
          }),
      },
    });
  };

  const queryClient = useQueryClient();
  const [_, copy] = useCopyToClipboard({
    description: t('copied_event.description'),
  });

  const handleToggleEvent = () => {
    toggleEventPrivacy(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: userKeys.lists() });
        if (eventStatus !== 'PRIVATE') copy(`${import.meta.env.VITE_PUBLIC_URL}/event/${eventId}`);
      },
    });
  };

  return (
    <Dropdown
      className='w-[13.5625rem] !z-[60]'
      trigger={
        <IconButton
          ariaLabel={t('toggle-menu')}
          className={'self-end md:self-start'}
          isLoading={isPending}
          hierarchy='tertiary'
          data-cy='event-flowflow-menu'
          icon={isPending ? undefined : () => <Icon icon='ellipsis-vertical-icon' />}
        />
      }
    >
      {options.viewEvent && (
        <DropdownItem asChild>
          <Link
            className='flex items-center !justify-between w-full gap-nano'
            target='_blank'
            to={`${import.meta.env.VITE_PUBLIC_URL}/event/${eventId}`}
          >
            {t('view-event')}
            <Icon icon='external-link-icon' />
          </Link>
        </DropdownItem>
      )}

      {options.eventPanel && (
        <DropdownItem hasDivider={!isEventListPage && eventStatus === 'DRAFT'} asChild>
          <Link to={`/manage/${eventId}`} className='flex items-center !justify-between w-full gap-nano'>
            {t('event-panel')}
            <Icon icon='bar-chart-big' />
          </Link>
        </DropdownItem>
      )}

      {options.edit && (
        <DropdownItem hasDivider={eventStatus === 'DRAFT'} asChild>
          <Link className='w-full' to={`${eventId}/details`}>
            {t('edit')}
          </Link>
        </DropdownItem>
      )}

      {options.tooglePrivate && (
        <DropdownItem onClick={handleToggleEvent}>
          <DropdownItemContent>
            <Switch
              className='flex w-full !justify-between flex-row-reverse [&>span]:text-ctx-content-base'
              checked={eventStatus === 'PRIVATE'}
              label={t('event-status.PRIVATE')}
            />
          </DropdownItemContent>
        </DropdownItem>
      )}

      {options.delete && (
        <DropdownItem onClick={openDeleteDialog} status='destructive'>
          <DropdownItemContent
            className='flex items-center justify-between w-full'
            trailingIcon={(props) => (
              <Icon
                {...props}
                className={cn(props.className, 'text-ctx-interactive-destructive')}
                icon='trash-2-icon'
              />
            )}
          >
            {t('delete-event')}
          </DropdownItemContent>
        </DropdownItem>
      )}
    </Dropdown>
  );
};

export default EventOverflowMenu;
